import React, { useEffect, useState } from "react";
import { db, storage } from "../config/firebaseConfig";
import {
  collection,
  getDoc,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { hasPermission, PERMISSIONS } from "../utils/roleAccess";

function AdminEventManager() {
  const [events, setEvents] = useState([]);
  const [canManageEvents, setCanManageEvents] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        setCanManageEvents(
          userSnap.exists() &&
            hasPermission(userSnap.data(), PERMISSIONS.MANAGE_EVENTS),
        );
      } else {
        setCanManageEvents(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "events"));
      setEvents(
        querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })),
      );
      setLoading(false);
    };
    fetchEvents();
  }, []);

  // Change status
  const handleStatusChange = async (eventId, newStatus) => {
    await updateDoc(doc(db, "events", eventId), { status: newStatus });
    setEvents((prev) =>
      prev.map((ev) => (ev.id === eventId ? { ...ev, status: newStatus } : ev)),
    );
  };

  // Delete file from event
  const handleDeleteFile = async (eventId, fileUrl) => {
    try {
      // Remove from storage
      const fileRef = ref(storage, fileUrl);
      await deleteObject(fileRef);

      // Remove from Firestore event
      const eventDoc = doc(db, "events", eventId);
      const event = events.find((ev) => ev.id === eventId);
      const updatedFiles = event.imageUrls.filter((url) => url !== fileUrl);
      await updateDoc(eventDoc, { imageUrls: updatedFiles });

      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === eventId ? { ...ev, imageUrls: updatedFiles } : ev,
        ),
      );
      alert("File deleted!");
    } catch (err) {
      alert("Error deleting file.");
    }
  };

  if (!canManageEvents) {
    return (
      <div className="flex items-center justify-center min-h-screen text-xl text-red-600">
        You do not have permission to manage events.
      </div>
    );
  }

  // Filter events by status
  const filteredEvents =
    statusFilter === "all"
      ? events
      : events.filter((event) => event.status === statusFilter);

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center">Event Manager</h2>
      {/* Status Filter Tabs */}
      <div className="flex justify-center gap-4 mb-8">
        <button
          className={`px-4 py-2 rounded-t-lg font-semibold ${
            statusFilter === "approved"
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setStatusFilter("approved")}
        >
          Approved
        </button>
        <button
          className={`px-4 py-2 rounded-t-lg font-semibold ${
            statusFilter === "rejected"
              ? "bg-red-600 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setStatusFilter("rejected")}
        >
          Rejected
        </button>
        <button
          className={`px-4 py-2 rounded-t-lg font-semibold ${
            statusFilter === "wip"
              ? "bg-yellow-500 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setStatusFilter("wip")}
        >
          WIP
        </button>
        <button
          className={`px-4 py-2 rounded-t-lg font-semibold ${
            statusFilter === "all"
              ? "bg-green-600 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setStatusFilter("all")}
        >
          All
        </button>
      </div>
      {loading ? (
        <div>Loading events...</div>
      ) : (
        filteredEvents.map((event) => (
          <div key={event.id} className="bg-white rounded-xl shadow p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <div>
                <div className="font-bold text-lg">{event.eventName}</div>
                <div className="text-sm text-gray-500">{event.date}</div>
              </div>
              <select
                value={event.status || "wip"}
                onChange={(e) => handleStatusChange(event.id, e.target.value)}
                className="border rounded px-2 py-1"
              >
                <option value="wip">Work In Progress</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="mb-2">{event.description}</div>
            <div className="flex flex-wrap gap-3">
              {event.imageUrls &&
                event.imageUrls.map((url, idx) => (
                  <div key={idx} className="relative">
                    {url.includes("mp4") ? (
                      <video
                        src={url}
                        controls
                        className="w-24 h-24 rounded border"
                      />
                    ) : (
                      <img
                        src={url}
                        alt="file"
                        className="w-24 h-24 object-cover rounded border"
                      />
                    )}
                    <button
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                      title="Delete file"
                      onClick={() => handleDeleteFile(event.id, url)}
                    >
                      ×
                    </button>
                  </div>
                ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default AdminEventManager;
